import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { sortBy } from 'lodash'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import { getGetPrams } from '../../../utils/url'
import { compile } from '../../../utils/search'

interface Props {
  callbackTeamIds: (teamIds: number[]) => void
  callbackMemberIds: (memberIds: number[]) => void
  isClear?: boolean
}

interface GetParams {
  team_ids: string
  member_ids: string
}

const SearchModule: FC<Props> = (props) => {
  const {
    callbackTeamIds,
    callbackMemberIds,
    isClear,
  } = props

  const {
    commonPart,
    memberList,
    current,
    analyze,
  } = useSelector((state: RootState) => state)

  const [isSearchDisplay, setIsSearchDisplay] = useState(false)
  const [teamIds, setTeamIds] = useState<number[]>([])
  const [memberIds, setMemberIds] = useState<number[]>([])
  const [isClickable, setIsClickable] = useState(true)

  const [teams, setTeams] = useState<{id: number, name: string}[]>([])
  const [members, setMembers] = useState<{id: number, name: string}[]>([])
  const [searchTeamWord, setSearchTeamWord] = useState<string>("")
  const [searchMemberWord, setSearchMemberWord] = useState<string>("")

  const teamsInit = useMemo(() => (
    commonPart.teams?.map((team) => ({ id: team.id, name: team.name })) || []
  ), [commonPart.teams])

  const membersInit = useMemo(() => (
    memberList.members.map((member) => ({ id: member.id, name: member.name })) || []
  ), [memberList.members])

  const clickTeam = (team_id: number) => {
    if (teamIds.includes(team_id)) {
      setTeamIds(teamIds.filter((id) => id !== team_id))
    } else {
      setTeamIds([...teamIds, team_id])
    }
  }

  const clickMember = (member_id: number) => {
    if (memberIds.includes(member_id)) {
      setMemberIds(memberIds.filter((id) => id !== member_id))
    } else {
      setMemberIds([...memberIds, member_id])
    }
  }

  const searchTeam = (words: string) => {
    setSearchTeamWord(words)
    if (!words) {
      setTeams([...teamsInit])
      return
    }
    const word = words.split(/\s+/)
    const list = teamsInit.filter((team) => {
      if (team === null) return false
      return (compile(word).test(team.name)) || teamIds.includes(team.id)
    })
    setTeams(sortBy(list, 'id'))
  }

  const resetTeam = () => {
    setSearchTeamWord("")
    setTeams([...teamsInit])
  }

  const searchMember = (words: string) => {
    setSearchMemberWord(words)
    if (!words) {
      setMembers([...membersInit])
      return
    }
    const word = words.split(/\s+/)
    const list = membersInit.filter((member) => {
      if (member === null) return false
      return (compile(word).test(member.name)) || memberIds.includes(member.id)
    })
    setMembers(sortBy(list, 'id'))
  }

  const resetMember = () => {
    setSearchMemberWord("")
    setMembers([...membersInit])
  }

  const openSearch = () => {
    setIsSearchDisplay(true)
  }

  const closeSearch = () => {
    const getParams = getGetPrams(location.search) as GetParams
    setIsSearchDisplay(false)
    resetTeam()
    resetMember()
    const getParamTeamIds = getParams.team_ids ? getParams.team_ids.split(',').map((v) => Number(v)) : []
    if (
      (getParamTeamIds.filter((value) => teamIds.indexOf(value) === -1).length)
      || (teamIds.filter((value) => getParamTeamIds.indexOf(value) === -1).length)
    ) {
      callbackTeamIds(teamIds)
    }
    const getParamMemberIds = getParams.member_ids ? getParams.member_ids.split(',').map((v) => Number(v)) : []
    if (
      (getParamMemberIds.filter((value) => memberIds.indexOf(value) === -1).length)
      || (memberIds.filter((value) => getParamMemberIds.indexOf(value) === -1).length)
    ) {
      callbackMemberIds(memberIds)
    }
  }

  useEffect(() => {
    const html = document.querySelector('html')
    if (!html) return
    if (isSearchDisplay) {
      html.classList.add('modal_open')
    } else {
      html.classList.remove('modal_open')
    }
  }, [isSearchDisplay])

  useEffect(() => {
    setTeams(commonPart.teams?.map((team) => ({ id: team.id, name: team.name })) || [])
  }, [commonPart.teams])

  useEffect(() => {
    setMembers(memberList.members?.map((member) => ({ id: member.id, name: member.name })) || [])
  }, [memberList.members])

  useEffect(() => {
    const getParams = getGetPrams(location.search) as GetParams
    if (getParams.team_ids) {
      setTeamIds(getParams.team_ids.split(',').map((value) => Number(value)))
    } else if (current.analyzeSelectTeam) {
      setTeamIds(current.analyzeSelectTeam.split(',').map((value) => Number(value)))
    }
    if (getParams.member_ids) {
      setMemberIds(getParams.member_ids.split(',').map((value) => Number(value)))
    } else if (current.analyzeSelectMember) {
      setMemberIds(current.analyzeSelectMember.split(',').map((value) => Number(value)))
    }
  }, [])

  useEffect(() => {
    if (!isClear) return
    setTeamIds([])
    setMemberIds([])
  }, [isClear])

  useEffect(() => {
    setIsClickable(!analyze.isLoading)
  }, [analyze.isLoading])

  return (
    <>
      <button
        className="search_button"
        type="button"
        onClick={() => openSearch()}
        disabled={!isClickable}
      >
        <FontAwesomeIcon icon={['fas', 'search']} />
        &nbsp;
        絞り込み
      </button>
      <div className={`search_box ${isSearchDisplay || 'none'}`}>
        <div className="search_detail">
          <div className="category">
            <div className="category_title">
              チーム
              {
                teamIds.length ? (
                  <div
                    className="select_reset"
                    role="none"
                    onClick={() => setTeamIds([])}
                  >
                    リセット
                  </div>
                ) : (
                  <></>
                )
              }
            </div>
            <div className="search_area">
              <FontAwesomeIcon
                icon={['fas', 'search']}
                className="icon"
              />
              <input
                type="text"
                placeholder="チームを検索"
                onChange={(event) => searchTeam(event.target.value)}
                value={searchTeamWord}
              />
              {
                searchTeamWord && (
                  <button type="button" className="reset" onClick={resetTeam}>
                    <FontAwesomeIcon icon={['far', 'times-circle']} />
                  </button>
                )
              }
            </div>
            {
              teams.map((team) => (
                <button
                  type="button"
                  className={`chip ${teamIds.includes(team.id) ? "checked" : ""}`}
                  key={`team_${team.id}`}
                  onClick={() => clickTeam(team.id)}
                >
                  {team.name}
                </button>
              ))
            }
          </div>
          <div className="category">
            <div className="category_title">
              メンバー
              {
                memberIds.length ? (
                  <div
                    className="select_reset"
                    role="none"
                    onClick={() => setMemberIds([])}
                  >
                    リセット
                  </div>
                ) : (
                  <></>
                )
              }
            </div>
            <div className="search_area">
              <FontAwesomeIcon
                icon={['fas', 'search']}
                className="icon"
              />
              <input
                type="text"
                placeholder="メンバーを検索"
                onChange={(event) => searchMember(event.target.value)}
                value={searchMemberWord}
              />
              {
                searchMemberWord && (
                  <button type="button" className="reset" onClick={resetMember}>
                    <FontAwesomeIcon icon={['far', 'times-circle']} />
                  </button>
                )
              }
            </div>
            {
              members.map((member) => (
                <button
                  type="button"
                  className={`chip ${memberIds.includes(member.id) ? "checked" : ""}`}
                  key={`member_${member.id}`}
                  onClick={() => clickMember(member.id)}
                >
                  {member.name}
                </button>
              ))
            }
          </div>
        </div>
      </div>
      <div className={`close_screen gray ${isSearchDisplay || 'none'}`} role="presentation" onClick={() => closeSearch()} />
    </>
  )
}

SearchModule.defaultProps = {
  isClear: false,
}

export default SearchModule
